import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Columns3, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Checkbox } from '@components/ui/checkbox'
import { Input } from '@components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@components/ui/dropdown-menu'
import { AppliedFilterPills, FilterField, FiltersPopover } from '@components/settings/table-filters'
import {
  createCalibrationRule,
  deleteCalibrationRule,
  listAdasCategories,
  listCalibrationRules,
  updateCalibrationRule
} from '../../../api/scrubApi'
import { getFriendlyErrorMessage } from '../../../utils/errorMessage'
import { FormModal, FormModalFooter } from '@components/branded-modal'

const ANY = '__any__'

// Operation labels as serialized by the BE (@JsonValue on the Operation enum);
// each value is both the display label and the filter param the BE parses back.
const OPERATION_OPTIONS = [
  'R&R',
  'R&I',
  'O/H',
  'REPAIR',
  'REFINISH',
  'Wheel Alignment',
  'SUBLET',
  'ADDITIONAL',
  'OVERLAP',
  'SCAN',
  'ANY COLLISION',
  'Collision',
  'INSPECTION/DAMAGE ASSESSMENT',
  'ANY',
  'RPR'
]

const CONFIDENCE_OPTIONS = ['High', 'Medium', 'Conditional']
const PRODUCT_TYPE_OPTIONS = ['Calibration', 'Service', 'Diagnostic', 'OEM Reference']

const PAGE_SIZES = [10, 20, 50, 100]

/** Format an ISO timestamp as "May 4, 2026". */
const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Column registry: key = DTO field (or synthetic), label = header text.
 *  `sortValue` extracts the comparable value; `render` the cell content.
 *  All columns are visible by default; any can be hidden from the Columns dropdown. */
const COLUMNS = [
  {
    key: 'ruleId',
    label: 'Rule ID',
    sortValue: (r) => r.ruleId || '',
    render: (r) => <span className='whitespace-nowrap font-mono font-medium text-[#0A1628]'>{r.ruleId}</span>
  },
  {
    key: 'oem',
    label: 'OEM',
    sortValue: (r) => r.oem || '',
    render: (r) => (r.oem ? <span className='whitespace-nowrap'>{r.oem}</span> : <span className='text-[#6B7280]'>—</span>)
  },
  { key: 'make', label: 'Make', sortValue: (r) => r.make || '' },
  {
    key: 'model',
    label: 'Model',
    sortValue: (r) => r.model || '',
    render: (r) =>
      r.model ? (
        <div className='max-w-[120px] truncate' title={r.model}>
          {r.model}
        </div>
      ) : (
        <span className='whitespace-nowrap text-[#6B7280]'>All models</span>
      )
  },
  {
    key: 'years',
    label: 'Years',
    sortValue: (r) => r.modelYearStart ?? 0,
    render: (r) => (
      <span className='whitespace-nowrap'>
        {r.modelYearStart}–{r.modelYearEnd}
      </span>
    )
  },
  { key: 'adasCategory', label: 'ADAS Category', sortValue: (r) => r.adasCategory || '' },
  { key: 'operation', label: 'Operation', sortValue: (r) => r.operation || '' },
  { key: 'calibrationMethod', label: 'Method', sortValue: (r) => r.calibrationMethod || '' },
  { key: 'productType', label: 'Product Type', sortValue: (r) => r.productType || '' },
  {
    key: 'confidence',
    label: 'Confidence',
    sortValue: (r) => r.confidence || '',
    render: (r) => {
      const palette = { High: '#059669', Medium: '#D97706', Conditional: '#6B7280' }
      const color = palette[r.confidence] || '#6B7280'
      return r.confidence ? (
        <div className='inline-flex items-center gap-1.5'>
          <span className='inline-block h-2 w-2 rounded-full' style={{ background: color }} />
          <span className='text-[12px] font-medium' style={{ color }}>
            {r.confidence}
          </span>
        </div>
      ) : null
    }
  },
  { key: 'sectionHeader', label: 'Section', sortValue: (r) => r.sectionHeader || '' },
  {
    key: 'partName',
    label: 'Part Name',
    sortValue: (r) => r.partName || '',
    render: (r) => (
      <div className='max-w-[200px] truncate' title={r.partName}>
        {r.partName}
      </div>
    )
  },
  { key: 'calibrationSystem', label: 'System', sortValue: (r) => r.calibrationSystem || '' },
  { key: 'calibrationTool', label: 'Tool', sortValue: (r) => r.calibrationTool || '' },
  {
    key: 'triggerReason',
    label: 'Trigger Reason',
    sortValue: (r) => r.triggerReason || '',
    render: (r) => (
      <div className='max-w-[240px] truncate' title={r.triggerReason}>
        {r.triggerReason}
      </div>
    )
  },
  {
    key: 'sourceDocument',
    label: 'Source Document',
    sortValue: (r) => r.sourceDocument || '',
    render: (r) => (
      <div className='max-w-[220px] truncate' title={r.sourceDocument}>
        {r.sourceDocument}
      </div>
    )
  },
  {
    key: 'createdAt',
    label: 'Created',
    sortValue: (r) => (r.createdAt ? new Date(r.createdAt).getTime() : 0),
    render: (r) => <span className='whitespace-nowrap text-[13px] text-[#475569]'>{formatDate(r.createdAt)}</span>
  }
]

const allColumnKeys = () => new Set(COLUMNS.map((c) => c.key))

const SortIcon = ({ direction }) => {
  if (direction === 'asc') return <ArrowUp className='h-3.5 w-3.5' />
  if (direction === 'desc') return <ArrowDown className='h-3.5 w-3.5' />
  return <ArrowUpDown className='h-3.5 w-3.5 opacity-50' />
}

const EMPTY_DRAFT = {
  ruleId: '',
  oem: '',
  make: '',
  model: '',
  partName: '',
  calibrationSystem: '',
  modelYearStart: '',
  modelYearEnd: '',
  adasCategory: '',
  operation: '',
  calibrationMethod: '',
  productType: '',
  confidence: ''
}

const isYear = (v) => /^\d{4}$/.test(String(v).trim())

/** Mirrors the BE's requireBaseFields guard so Save is only enabled for payloads
 *  that can pass validation (ruleId is only client-supplied in create mode). */
const isDraftValid = (draft, isCreate) =>
  Boolean(
    draft &&
      (!isCreate || /^[A-Z]{2,4}-\d{4}$/.test(draft.ruleId.trim())) &&
      draft.oem.trim() &&
      draft.make.trim() &&
      draft.partName.trim() &&
      draft.operation &&
      draft.calibrationSystem.trim() &&
      draft.calibrationMethod.trim() &&
      draft.confidence &&
      draft.productType &&
      isYear(draft.modelYearStart) &&
      isYear(draft.modelYearEnd)
  )

/**
 * Settings → Calibration Rules: browser over the ADAS Intelligence calibration
 * rule library (GET /v1/intelligence/rules). Server-side filtering + pagination;
 * the BE exposes no sort param, so column sorting is client-side over the
 * currently loaded page. Add/edit/delete call the rule-maintenance CRUD endpoints
 * (POST / PUT /{ruleId} / DELETE /{ruleId}?reason=…) and refetch on success.
 */
export const RulesSettings = () => {
  // Committed (debounced) filter values. make/model/oem/adasCategory/operation/active
  // drive the fetch (server-side); ruleId and productType are applied client-side over
  // the loaded page — the BE list endpoint doesn't support them.
  const [filters, setFilters] = useState({
    ruleId: '',
    make: '',
    model: '',
    oem: '',
    adasCategory: '',
    operation: '',
    productType: '',
    active: 'true'
  })
  // Raw text-input values, debounced into `filters`.
  const [ruleIdInput, setRuleIdInput] = useState('')
  const [makeInput, setMakeInput] = useState('')
  const [modelInput, setModelInput] = useState('')
  const [oemInput, setOemInput] = useState('')

  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [rows, setRows] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const [categories, setCategories] = useState([])
  const [sort, setSort] = useState({ key: null, direction: null })
  const [visibleKeys, setVisibleKeys] = useState(allColumnKeys)

  // Row selection (quotes-page pattern) + add/edit/delete dialog state.
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [editRow, setEditRow] = useState(null) // original rule behind the edit dialog; null in create mode
  const [editDraft, setEditDraft] = useState(null) // editable field values; dialog is open while non-null
  const [deleteIds, setDeleteIds] = useState(null) // rule ids pending delete confirm
  const [deleteReason, setDeleteReason] = useState('') // required by DELETE …?reason=…
  const [isMutating, setIsMutating] = useState(false)
  const [mutationError, setMutationError] = useState(null) // surfaced inline in the open dialog
  // Bumped after a successful create/update/delete to refetch the canonical list.
  const [refreshKey, setRefreshKey] = useState(0)

  // Debounce the free-text inputs into the committed filters (and reset paging).
  useEffect(() => {
    const h = setTimeout(() => {
      setFilters((prev) => {
        if (prev.ruleId === ruleIdInput && prev.make === makeInput && prev.model === modelInput && prev.oem === oemInput) return prev
        setPage(0)
        return { ...prev, ruleId: ruleIdInput, make: makeInput, model: modelInput, oem: oemInput }
      })
    }, 350)
    return () => clearTimeout(h)
  }, [ruleIdInput, makeInput, modelInput, oemInput])

  useEffect(() => {
    let cancelled = false
    listAdasCategories()
      .then((list) => {
        if (!cancelled) setCategories(list)
      })
      .catch(() => {
        /* dropdowns just stay empty; the page itself still works */
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await listCalibrationRules({
          page,
          size,
          make: filters.make,
          model: filters.model,
          oem: filters.oem,
          adasCategory: filters.adasCategory,
          operation: filters.operation,
          active: filters.active === 'true'
        })
        if (cancelled) return
        setRows(Array.isArray(data?.content) ? data.content : [])
        setTotalElements(data?.totalElements ?? 0)
        setTotalPages(data?.totalPages ?? 0)
      } catch (err) {
        if (!cancelled) setError(getFriendlyErrorMessage(err, 'Failed to load calibration rules'))
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // Depend on the server-side filter fields only — ruleId/productType are
    // client-side and must not retrigger the fetch. refreshKey re-runs the fetch
    // after a successful create/update/delete.
  }, [page, size, filters.make, filters.model, filters.oem, filters.adasCategory, filters.operation, filters.active, refreshKey])

  const setSelectFilter = useCallback((key, value) => {
    setPage(0)
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  // Every filter lives in the popover; the badge counts the non-default ones.
  const popoverFilterCount = [
    filters.ruleId,
    filters.make,
    filters.model,
    filters.oem,
    filters.adasCategory,
    filters.operation,
    filters.productType,
    filters.active !== 'true'
  ].filter(Boolean).length

  const hasActiveFilters = popoverFilterCount > 0

  const clearFilters = () => {
    setRuleIdInput('')
    setMakeInput('')
    setModelInput('')
    setOemInput('')
    setPage(0)
    setFilters({ ruleId: '', make: '', model: '', oem: '', adasCategory: '', operation: '', productType: '', active: 'true' })
  }

  // Per-pill clear for the text filters: reset the raw input too, so the debounce
  // effect doesn't immediately re-commit the cleared value.
  const clearTextFilter = (key) => {
    if (key === 'ruleId') setRuleIdInput('')
    if (key === 'make') setMakeInput('')
    if (key === 'model') setModelInput('')
    if (key === 'oem') setOemInput('')
    setPage(0)
    setFilters((prev) => ({ ...prev, [key]: '' }))
  }

  const appliedPills = [
    filters.ruleId && { key: 'ruleId', label: 'Rule ID', value: filters.ruleId, onClear: () => clearTextFilter('ruleId') },
    filters.make && { key: 'make', label: 'Make', value: filters.make, onClear: () => clearTextFilter('make') },
    filters.model && { key: 'model', label: 'Model', value: filters.model, onClear: () => clearTextFilter('model') },
    filters.oem && { key: 'oem', label: 'OEM', value: filters.oem, onClear: () => clearTextFilter('oem') },
    filters.adasCategory && {
      key: 'adasCategory',
      label: 'Category',
      value: filters.adasCategory,
      onClear: () => setSelectFilter('adasCategory', '')
    },
    filters.operation && {
      key: 'operation',
      label: 'Operation',
      value: filters.operation,
      onClear: () => setSelectFilter('operation', '')
    },
    filters.productType && {
      key: 'productType',
      label: 'Product Type',
      value: filters.productType,
      onClear: () => setSelectFilter('productType', '')
    },
    filters.active !== 'true' && { key: 'active', label: 'Status', value: 'Inactive', onClear: () => setSelectFilter('active', 'true') }
  ].filter(Boolean)

  // Cycle a header through asc → desc → unsorted.
  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const visibleColumns = useMemo(() => COLUMNS.filter((c) => visibleKeys.has(c.key)), [visibleKeys])

  // Rule ID (contains) and Product Type (exact) filter client-side over the loaded
  // page, since the BE list endpoint doesn't accept them.
  const filteredRows = useMemo(() => {
    const q = filters.ruleId.trim().toLowerCase()
    return rows.filter((r) => {
      if (q && !(r.ruleId || '').toLowerCase().includes(q)) return false
      if (filters.productType && r.productType !== filters.productType) return false
      return true
    })
  }, [rows, filters.ruleId, filters.productType])

  const sortedRows = useMemo(() => {
    if (!sort.key) return filteredRows
    const col = COLUMNS.find((c) => c.key === sort.key)
    if (!col) return filteredRows
    const dir = sort.direction === 'desc' ? -1 : 1
    return [...filteredRows].sort((a, b) => {
      const va = col.sortValue(a)
      const vb = col.sortValue(b)
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
      return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base', numeric: true }) * dir
    })
  }, [filteredRows, sort])

  const toggleColumn = (key) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        if (next.size === 1) return prev // keep at least one column visible
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  // ── Selection / add / edit / delete ────────────────────────────────────────

  const toggleSelect = (rule) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(rule.ruleId)) next.delete(rule.ruleId)
      else next.add(rule.ruleId)
      return next
    })
  }

  const openEdit = (rule) => {
    setMutationError(null)
    setEditRow(rule)
    setEditDraft({
      ruleId: rule.ruleId || '',
      oem: rule.oem || '',
      make: rule.make || '',
      model: rule.model || '',
      partName: rule.partName || '',
      calibrationSystem: rule.calibrationSystem || '',
      modelYearStart: rule.modelYearStart != null ? String(rule.modelYearStart) : '',
      modelYearEnd: rule.modelYearEnd != null ? String(rule.modelYearEnd) : '',
      adasCategory: rule.adasCategory || '',
      operation: rule.operation || '',
      calibrationMethod: rule.calibrationMethod || '',
      productType: rule.productType || '',
      confidence: rule.confidence || ''
    })
  }

  // Create mode reuses the edit dialog with empty fields (editRow stays null).
  const openCreate = () => {
    setMutationError(null)
    setEditRow(null)
    setEditDraft({ ...EMPTY_DRAFT })
  }

  const closeEditDialog = () => {
    setEditRow(null)
    setEditDraft(null)
    setMutationError(null)
  }

  const openDelete = (ids) => {
    setMutationError(null)
    setDeleteReason('')
    setDeleteIds(ids)
  }

  const closeDeleteDialog = () => {
    setDeleteIds(null)
    setDeleteReason('')
    setMutationError(null)
  }

  /** CalibrationRuleRequestDto payload: camelCase strings, years as 4-digit strings.
   *  Unedited fields (notes, source document, …) are carried over from the original
   *  row on update so the PUT doesn't blank them. */
  const buildPayload = () => ({
    ruleId: editRow ? editRow.ruleId : editDraft.ruleId.trim(),
    oem: editDraft.oem.trim(),
    make: editDraft.make.trim(),
    model: editDraft.model.trim() || null,
    partName: editDraft.partName.trim(),
    calibrationSystem: editDraft.calibrationSystem.trim(),
    modelYearStart: String(editDraft.modelYearStart).trim(),
    modelYearEnd: String(editDraft.modelYearEnd).trim(),
    adasCategory: editDraft.adasCategory || null,
    operation: editDraft.operation,
    calibrationMethod: editDraft.calibrationMethod.trim(),
    productType: editDraft.productType,
    confidence: editDraft.confidence,
    calibrationTool: editRow?.calibrationTool ?? null,
    sectionHeader: editRow?.sectionHeader ?? null,
    applicabilityNotes: editRow?.applicabilityNotes ?? null,
    triggerReason: editRow?.triggerReason ?? null,
    sourceDocument: editRow?.sourceDocument ?? null,
    oemReference: editRow?.oemReference ?? null,
    documentSection: editRow?.documentSection ?? null,
    documentTitle: editRow?.documentTitle ?? null,
    displayDocumentName: editRow?.displayDocumentName ?? null,
    procedureSteps: editRow?.procedureSteps ?? null,
    repairLinePhrase: editRow?.repairLinePhrase ?? null
  })

  const saveEdit = async () => {
    setIsMutating(true)
    setMutationError(null)
    try {
      const payload = buildPayload()
      if (editRow) await updateCalibrationRule(editRow.ruleId, payload)
      else await createCalibrationRule(payload)
      closeEditDialog()
      setRefreshKey((k) => k + 1)
    } catch (err) {
      setMutationError(err.message)
    } finally {
      setIsMutating(false)
    }
  }

  const confirmDelete = async () => {
    setIsMutating(true)
    setMutationError(null)
    try {
      await Promise.all(deleteIds.map((id) => deleteCalibrationRule(id, deleteReason.trim())))
      setSelectedIds((prev) => {
        const next = new Set(prev)
        deleteIds.forEach((id) => next.delete(id))
        return next
      })
      closeDeleteDialog()
      setRefreshKey((k) => k + 1)
    } catch (err) {
      setMutationError(err.message)
    } finally {
      setIsMutating(false)
    }
  }

  return (
    <div className='mx-auto max-w-[1400px] mt-9'>
      {/* Page header */}
      <div className='mb-5'>
        <h1 className='text-[32px] font-bold leading-tight text-foreground'>Calibration Rules</h1>
        <p className='mt-1 text-[14px] text-muted-foreground'>
          The rule library that maps vehicle make, model, and ADAS category to required calibrations.
        </p>
      </div>

      {/* Filters toolbar — every filter lives in the Filters popover */}
      <div className='mb-4 flex flex-wrap items-center gap-3'>
        <FiltersPopover activeCount={popoverFilterCount}>
          <FilterField label='Rule ID'>
            <Input
              value={ruleIdInput}
              onChange={(e) => setRuleIdInput(e.target.value)}
              placeholder='e.g. ACU-0001'
              className='h-9 bg-white font-mono'
              aria-label='Filter by rule ID'
            />
          </FilterField>
          <FilterField label='Make'>
            <Input
              value={makeInput}
              onChange={(e) => setMakeInput(e.target.value)}
              placeholder='Any make'
              className='h-9 bg-white'
              aria-label='Filter by make'
            />
          </FilterField>
          <FilterField label='Model'>
            <Input
              value={modelInput}
              onChange={(e) => setModelInput(e.target.value)}
              placeholder='Any model'
              className='h-9 bg-white'
              aria-label='Filter by model'
            />
          </FilterField>
          <FilterField label='OEM'>
            <Input
              value={oemInput}
              onChange={(e) => setOemInput(e.target.value)}
              placeholder='Any OEM'
              className='h-9 bg-white'
              aria-label='Filter by OEM'
            />
          </FilterField>
          <FilterField label='ADAS Category'>
            <Select value={filters.adasCategory || ANY} onValueChange={(v) => setSelectFilter('adasCategory', v === ANY ? '' : v)}>
              <SelectTrigger className='bg-white' aria-label='Filter by ADAS category'>
                <SelectValue placeholder='All Categories' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All Categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label='Operation'>
            <Select value={filters.operation || ANY} onValueChange={(v) => setSelectFilter('operation', v === ANY ? '' : v)}>
              <SelectTrigger className='bg-white' aria-label='Filter by operation'>
                <SelectValue placeholder='All Operations' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All Operations</SelectItem>
                {OPERATION_OPTIONS.map((op) => (
                  <SelectItem key={op} value={op}>
                    {op}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label='Product Type'>
            <Select value={filters.productType || ANY} onValueChange={(v) => setSelectFilter('productType', v === ANY ? '' : v)}>
              <SelectTrigger className='bg-white' aria-label='Filter by product type'>
                <SelectValue placeholder='All Product Types' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All Product Types</SelectItem>
                {PRODUCT_TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label='Status'>
            <Select value={filters.active} onValueChange={(v) => setSelectFilter('active', v)}>
              <SelectTrigger className='bg-white' aria-label='Filter by status'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='true'>Active</SelectItem>
                <SelectItem value='false'>Inactive</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </FiltersPopover>

        {/* Add + column visibility */}
        <div className='ml-auto flex items-center gap-2'>
          <Button size='sm' className='bg-sidebar text-white hover:bg-sidebar/90' onClick={openCreate}>
            <Plus className='mr-1 h-4 w-4' />
            Add Rule
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='outline' size='sm' className='bg-white'>
                <Columns3 className='mr-1.5 h-4 w-4' />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='max-h-[320px] w-[220px] overflow-y-auto'>
              <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={visibleKeys.has(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Applied filters */}
      <AppliedFilterPills pills={appliedPills} onClearAll={clearFilters} />

      {error && <div className='mb-4 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>{error}</div>}

      {/* Selection action bar (quotes-page pattern) */}
      {selectedIds.size > 0 && (
        <div
          className='mb-3 flex items-center justify-between gap-3 rounded-lg px-4 py-2.5 text-white'
          style={{ background: 'var(--primary-gradient)' }}
        >
          <span className='text-[14px] font-semibold'>{selectedIds.size} selected</span>
          <div className='flex items-center gap-2'>
            <button
              onClick={() => openDelete(Array.from(selectedIds))}
              className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#B91C1C] transition-colors hover:bg-[#F8FAFC]'
            >
              <Trash2 className='h-3.5 w-3.5' />
              Delete
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className='overflow-hidden rounded-lg border border-[#D1D5D8] bg-white'>
        <div className='overflow-x-auto'>
          <Table>
            <TableHeader>
              <TableRow className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'>
                <TableHead className='w-12 text-white' aria-label='Selection' />
                {visibleColumns.map((col) => (
                  <TableHead key={col.key} className='whitespace-nowrap text-white'>
                    <button
                      type='button'
                      onClick={() => toggleSort(col.key)}
                      className='inline-flex items-center gap-1.5 hover:text-white/80'
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      <SortIcon direction={sort.key === col.key ? sort.direction : null} />
                    </button>
                  </TableHead>
                ))}
                {/* Sticky on the right; solid bg (gradients don't inherit) so scrolling columns pass under it */}
                <TableHead className='sticky right-0 z-10 w-20 bg-[#1F2D3D] text-white shadow-[-8px_0_8px_-8px_rgba(15,23,42,0.3)]'>
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 2} className='py-12 text-center'>
                    <Loader2 className='mx-auto h-5 w-5 animate-spin text-[#1F6FEB]' />
                    <div className='mt-2 text-sm text-[#6B7280]'>Loading rules...</div>
                  </TableCell>
                </TableRow>
              ) : sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 2} className='py-10 text-center text-sm text-[#6B7280]'>
                    {hasActiveFilters ? 'No rules match the selected filters.' : 'No calibration rules found.'}
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((rule) => {
                  const isSelected = selectedIds.has(rule.ruleId)
                  return (
                    <TableRow
                      key={rule.ruleId}
                      data-state={isSelected ? 'selected' : undefined}
                      className='h-12 bg-white even:bg-table-row-even hover:bg-table-row-hover data-[state=selected]:bg-[#E8EFFF] data-[state=selected]:hover:bg-[#E8EFFF]'
                    >
                      <TableCell className='align-middle'>
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(rule)} aria-label={`Select ${rule.ruleId}`} />
                      </TableCell>
                      {visibleColumns.map((col) => (
                        <TableCell key={col.key} className='align-middle text-[13px] text-[#0A1628]'>
                          {col.render ? col.render(rule) : col.sortValue(rule) || <span className='text-[#6B7280]'>—</span>}
                        </TableCell>
                      ))}
                      {/* bg-inherit follows the row's stripe/hover/selected color */}
                      <TableCell className='sticky right-0 z-10 bg-inherit align-middle shadow-[-8px_0_8px_-8px_rgba(15,23,42,0.15)]'>
                        <div className='flex items-center gap-1'>
                          <button
                            type='button'
                            onClick={() => openEdit(rule)}
                            aria-label={`Edit ${rule.ruleId}`}
                            className='flex h-7 w-7 items-center justify-center rounded-md text-[#475569] hover:bg-[#EFF6FF] hover:text-[#1F6FEB]'
                          >
                            <Pencil className='h-3.5 w-3.5' />
                          </button>
                          <button
                            type='button'
                            onClick={() => openDelete([rule.ruleId])}
                            aria-label={`Delete ${rule.ruleId}`}
                            className='flex h-7 w-7 items-center justify-center rounded-md text-[#475569] hover:bg-[#FEF2F2] hover:text-[#B91C1C]'
                          >
                            <Trash2 className='h-3.5 w-3.5' />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination footer */}
      <div className='mt-3 flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <div className='text-[12px] text-[#6B7280]'>
            {totalElements > 0
              ? `Showing ${page * size + 1}–${Math.min((page + 1) * size, totalElements)} of ${totalElements}`
              : 'No results'}
          </div>
          <Select
            value={String(size)}
            onValueChange={(v) => {
              setPage(0)
              setSize(Number(v))
            }}
          >
            <SelectTrigger className='h-8 w-[130px] bg-white text-[12px]' aria-label='Rows per page'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {s} per page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='flex items-center gap-1'>
          <Button variant='outline' size='sm' disabled={page <= 0 || isLoading} onClick={() => setPage(Math.max(0, page - 1))}>
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <span className='px-2 text-[12px] text-[#6B7280]'>
            Page {page + 1} / {Math.max(totalPages, 1)}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= totalPages - 1 || isLoading}
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      </div>

      {/* Add / edit dialog — pre-filled from the selected rule, empty in create mode */}
      <FormModal
        open={!!editDraft}
        onClose={closeEditDialog}
        title={editRow ? `Edit rule ${editRow.ruleId}` : 'Add rule'}
        subtitle={
          editRow
            ? 'Update this calibration rule. The change is written to the rule history.'
            : 'Define a new calibration rule. It runs the same validation as the xlsx upload.'
        }
        width='max-w-[480px]'
        footer={
          <FormModalFooter
            onCancel={closeEditDialog}
            cancelDisabled={isMutating}
            onConfirm={saveEdit}
            confirmDisabled={isMutating || !isDraftValid(editDraft, !editRow)}
            confirmLabel={
              <>
                {isMutating && <Loader2 className='h-4 w-4 animate-spin' />}
                {editRow ? 'Save' : 'Add'}
              </>
            }
          />
        }
      >
        {editDraft && (
          <div className='flex flex-col gap-4 py-2'>
            {!editRow && (
              <FilterField label='Rule ID (e.g. ACU-0001)'>
                <Input
                  value={editDraft.ruleId}
                  onChange={(e) => setEditDraft((d) => ({ ...d, ruleId: e.target.value.toUpperCase() }))}
                  placeholder='XX-0000'
                  className='font-mono'
                  aria-label='Rule ID'
                />
              </FilterField>
            )}
            <div className='grid grid-cols-2 gap-3'>
              <FilterField label='OEM'>
                <Input value={editDraft.oem} onChange={(e) => setEditDraft((d) => ({ ...d, oem: e.target.value }))} aria-label='OEM' />
              </FilterField>
              <FilterField label='Make'>
                <Input value={editDraft.make} onChange={(e) => setEditDraft((d) => ({ ...d, make: e.target.value }))} aria-label='Make' />
              </FilterField>
            </div>
            <FilterField label='Model (semicolon-separated, empty = all models)'>
              <Input
                value={editDraft.model}
                onChange={(e) => setEditDraft((d) => ({ ...d, model: e.target.value }))}
                placeholder='All models'
                aria-label='Model'
              />
            </FilterField>
            <div className='grid grid-cols-2 gap-3'>
              <FilterField label='Year Start'>
                <Input
                  type='number'
                  value={editDraft.modelYearStart}
                  onChange={(e) => setEditDraft((d) => ({ ...d, modelYearStart: e.target.value }))}
                  aria-label='Model year start'
                />
              </FilterField>
              <FilterField label='Year End'>
                <Input
                  type='number'
                  value={editDraft.modelYearEnd}
                  onChange={(e) => setEditDraft((d) => ({ ...d, modelYearEnd: e.target.value }))}
                  aria-label='Model year end'
                />
              </FilterField>
            </div>
            <FilterField label='ADAS Category'>
              <Select
                value={editDraft.adasCategory || ANY}
                onValueChange={(v) => setEditDraft((d) => ({ ...d, adasCategory: v === ANY ? '' : v }))}
              >
                <SelectTrigger className='bg-white' aria-label='ADAS category'>
                  <SelectValue placeholder='Select a category' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>None</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
            <FilterField label='Operation'>
              <Select
                value={editDraft.operation || ANY}
                onValueChange={(v) => setEditDraft((d) => ({ ...d, operation: v === ANY ? '' : v }))}
              >
                <SelectTrigger className='bg-white' aria-label='Operation'>
                  <SelectValue placeholder='Select an operation' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>None</SelectItem>
                  {OPERATION_OPTIONS.map((op) => (
                    <SelectItem key={op} value={op}>
                      {op}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
            <FilterField label='Part Name'>
              <Input
                value={editDraft.partName}
                onChange={(e) => setEditDraft((d) => ({ ...d, partName: e.target.value }))}
                placeholder='e.g. Front Radar Sensor'
                aria-label='Part name'
              />
            </FilterField>
            <div className='grid grid-cols-2 gap-3'>
              <FilterField label='Calibration System'>
                <Input
                  value={editDraft.calibrationSystem}
                  onChange={(e) => setEditDraft((d) => ({ ...d, calibrationSystem: e.target.value }))}
                  aria-label='Calibration system'
                />
              </FilterField>
              <FilterField label='Calibration Method'>
                <Input
                  value={editDraft.calibrationMethod}
                  onChange={(e) => setEditDraft((d) => ({ ...d, calibrationMethod: e.target.value }))}
                  placeholder='e.g. Static + Dynamic'
                  aria-label='Calibration method'
                />
              </FilterField>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <FilterField label='Product Type'>
                <Select
                  value={editDraft.productType || ANY}
                  onValueChange={(v) => setEditDraft((d) => ({ ...d, productType: v === ANY ? '' : v }))}
                >
                  <SelectTrigger className='bg-white' aria-label='Product type'>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>None</SelectItem>
                    {PRODUCT_TYPE_OPTIONS.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label='Confidence'>
                <Select
                  value={editDraft.confidence || ANY}
                  onValueChange={(v) => setEditDraft((d) => ({ ...d, confidence: v === ANY ? '' : v }))}
                >
                  <SelectTrigger className='bg-white' aria-label='Confidence'>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>None</SelectItem>
                    {CONFIDENCE_OPTIONS.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
            </div>
          </div>
        )}
        {mutationError && (
          <div className='rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>{mutationError}</div>
        )}
      </FormModal>

      {/* Delete confirm dialog — the BE requires a reason, recorded in the rule history */}
      <FormModal
        open={!!deleteIds}
        onClose={closeDeleteDialog}
        title={`Delete ${deleteIds?.length > 1 ? `${deleteIds.length} rules` : 'rule'}?`}
        width='max-w-[420px]'
        footer={
          <FormModalFooter
            onCancel={closeDeleteDialog}
            cancelDisabled={isMutating}
            onConfirm={confirmDelete}
            confirmDisabled={isMutating || !deleteReason.trim()}
            tone='danger'
            confirmLabel={
              <>
                {isMutating && <Loader2 className='h-4 w-4 animate-spin' />}
                Delete
              </>
            }
          />
        }
      >
        <p className='mb-4 text-[13px] leading-5 text-[#334155]'>
          {deleteIds?.length > 1 ? 'The selected rules will be soft-deleted.' : `Rule ${deleteIds?.[0]} will be soft-deleted.`} The reason
          is recorded in the rule history.
        </p>
        <FilterField label='Reason'>
          <Input
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder='Why is this rule being removed?'
            aria-label='Delete reason'
          />
        </FilterField>
        {mutationError && (
          <div className='rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>{mutationError}</div>
        )}
      </FormModal>
    </div>
  )
}
